// lib
import { Map } from 'immutable'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import useMountedRef from '@aytm/lib/hooks/useMountedRef'
import { fromEvent as ObservableFromEvent, merge as rxMerge } from 'rxjs'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { map as rxMap, mapTo as rxMapTo, mergeMap as rxMergeMap, takeUntil as rxTakeUntil } from 'rxjs/operators'

// styles
import '@aytm/lib/rfd-ui/styles/target-market/sample-size.scss'

// <Component>
SampleSize.propTypes = {
  data: PropTypes.instanceOf(Map).isRequired, // Map({ value, ranges, positions, minSampleSize, maxSampleSize })
  readOnly: PropTypes.bool,
  onUpdate: PropTypes.func,
  className: PropTypes.string,
  sliderWidth: PropTypes.number.isRequired // pixels
}
export default React.memo(
  SampleSize,
  (prevProps, nextProps) =>
    prevProps.data == nextProps.data &&
    prevProps.locale == nextProps.locale &&
    prevProps.onUpdate == nextProps.onUpdate &&
    prevProps.readOnly == nextProps.readOnly &&
    prevProps.className == nextProps.className
)
export function SampleSize(props, _context) {
  const mountedRef = useMountedRef()
  const { data: dataProp, onUpdate: onUpdateProp } = props
  const { readOnly, className, sliderWidth } = props
  const sliderRef = useRef(null)
  const dragHandleRef = useRef(null)

  // init prop data
  const [data, setData] = useState(dataStateMap(dataProp))

  const { value, ranges, positions, sliderMax, sliderMin, minInputSampleSize } = data

  // onUpdate
  const onUpdate = useCallback(
    value => void ('function' === typeof onUpdateProp && onUpdateProp(value)),
    [onUpdateProp]
  )
  const onUpdateRef = useRef(null)
  onUpdateRef.current = onUpdate

  // sample size
  const [sampleSize, setSampleSize] = useState(validateSampleSize(value, sliderMin, sliderMax))

  // sample size input
  const sampleSizeInputRef = useRef(null)
  const updateSampleSizeOnKeyPress = useCallback(
    event => {
      if (13 === event.keyCode && sampleSizeInputRef.current) {
        const value = validateSampleSize(event.target.value, minInputSampleSize, sliderMax)
        sampleSizeInputRef.current.value = value
        onUpdate(value)
      }
    },
    [sliderMax, onUpdate, minInputSampleSize]
  )
  // on sampleSize input blur
  const onInputBlur = () => {
    if (sampleSizeInputRef.current) {
      const value = validateSampleSize(sampleSizeInputRef.current.value, minInputSampleSize, sliderMax)
      sampleSizeInputRef.current.value = value
      onUpdate(value)
    }
  }

  // slider position
  const [sliderPosition, setSliderPosition] = useState(getSliderPosition(sampleSize, positions))
  useEffect(() => {
    if (sampleSizeInputRef.current) {
      sampleSizeInputRef.current.value = sampleSize
    }
    setSliderPosition(getSliderPosition(sampleSize, positions))
  }, [positions, sampleSize])

  // ranges
  const rangesList = useMemo(
    () =>
      ranges.map(range => {
        const from = positionIdBySampleSize(range.from, positions)
        const to = positionIdBySampleSize(range.to, positions)
        const width = (sliderWidth / positions.length) * (to - from)
        return { ...range, width }
      }),
    [sliderWidth, ranges, positions]
  )
  const activeRange = getActiveRange(sampleSize, rangesList)
  const sliderBgStyle = {
    width: (sampleSize <= positions[0] ? 0 : sliderPosition + 1).toString() + '%',
    backgroundColor: activeRange.bgColor
  }

  // drag slider subscription
  const unsubscribeRef = useRef(null)
  useEffect(() => {
    if (unsubscribeRef.current && 'function' == typeof unsubscribeRef.current) {
      unsubscribeRef.current()
      unsubscribeRef.current = null
    }
    if (!readOnly) {
      unsubscribeRef.current = dragArrow({
        sampleSizeInputRef,
        dragHandleRef,
        setSampleSize,
        onUpdateRef,
        sliderMax,
        sliderMin,
        positions,
        stepSize: sliderWidth / positions.length
      })
    }
    return unsubscribeRef.current ? unsubscribeRef.current : void 0
  }, [positions, readOnly, sliderMax, sliderMin, sliderWidth])

  // on data prop update
  useEffect(() => {
    if (mountedRef.current) {
      const newSampleSize = dataProp.get('value')
      sampleSizeInputRef.current.value = newSampleSize
      setData(dataStateMap(dataProp))
      setSampleSize(validateSampleSize(newSampleSize, minInputSampleSize, sliderMax))
    }
  }, [dataProp, sliderMax, mountedRef, minInputSampleSize])

  const onSliderClick = event => {
    const slider = sliderRef.current.getBoundingClientRect()
    const id = Math.min(
      positions.length - 1,
      Math.round(((positions.length - 1) / slider.width) * (event.clientX - slider.x))
    )
    const newSampleSize = validateSampleSize(positions[id], sliderMin, sliderMax)
    sampleSizeInputRef.current.value = newSampleSize
    onUpdate(newSampleSize)
  }

  return (
    <div
      className={classNames({
        ['rfd-ui-sample-size']: true,
        [className]: className,
        ['rfd-ui-sample-size-read-only']: readOnly
      })}
    >
      <div className="rfd-ui-sample-size-slider" style={{ width: `${sliderWidth}px` }} ref={sliderRef}>
        <span className="rfd-ui-sample-size-click" onClick={readOnly ? null : onSliderClick} />
        <span className="rfd-ui-sample-size-slider-bg" style={sliderBgStyle} />
        <span className="rfd-ui-slider-handle" ref={dragHandleRef} style={{ left: `${sliderPosition}%` }}>
          <span className="rfd-ui-slider-arrow-label">{sampleSize}</span>
          <div className="rfd-ui-slider-arrow">{renderArrow()}</div>
        </span>
        <div className="rfd-ui-sample-size-slider-labels" onClick={readOnly ? null : onSliderClick}>
          {rangesList.map((range, id) => (
            <span
              key={id}
              style={{ width: range.width }}
              className={classNames({ 'rfd-ui-sample-size-slider-labels-active': activeRange.label == range.label })}
            >
              {range.label}
            </span>
          ))}
        </div>
      </div>
      <div className="rfd-ui-sample-size-input">
        <input
          ref={sampleSizeInputRef}
          type="number"
          maxLength={6}
          onBlur={readOnly ? null : onInputBlur}
          readOnly={Boolean(readOnly)}
          onKeyDown={readOnly ? null : updateSampleSizeOnKeyPress}
          defaultValue={sampleSize}
        />
      </div>
    </div>
  )
}

function renderArrow() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 28 28">
      <filter id="shadow">
        <feDropShadow dx="1" dy="0.7" stdDeviation="1" floodColor="#AAAAAA" />
      </filter>
      <g id="Layer_2" data-name="Layer 2">
        <g id="Layer_4" data-name="Layer 4">
          <path
            filter="url(#shadow)"
            className="rfd-ui-slider-arrow-border"
            d="M10,1.06a.81.81,0,0,1,.76.45L18.89,20a.59.59,0,0,1-.07.61.85.85,0,0,1-.7.33H1.88a.85.85,0,0,1-.7-.33A.59.59,0,0,1,1.11,20L9.24,1.51A.81.81,0,0,1,10,1.06M10,0A1.85,1.85,0,0,0,8.27,1.09L.15,19.57a1.64,1.64,0,0,0,.17,1.65A1.87,1.87,0,0,0,1.88,22H18.12a1.87,1.87,0,0,0,1.56-.78,1.64,1.64,0,0,0,.17-1.65L11.73,1.09A1.85,1.85,0,0,0,10,0Z"
          />
          <path
            className="rfd-ui-slider-arrow-bg"
            d="M1.88,21.47a1.34,1.34,0,0,1-1.13-.56,1.1,1.1,0,0,1-.12-1.13L8.75,1.3a1.4,1.4,0,0,1,2.5,0l8.12,18.48a1.1,1.1,0,0,1-.12,1.13,1.34,1.34,0,0,1-1.13.56Z"
          />
        </g>
      </g>
    </svg>
  )
}

function dataStateMap(data = Map()) {
  return {
    value: data.get('value'),
    ranges: data.get('ranges'),
    positions: data.get('positions'),
    sliderMin: data.get('sliderMin'),
    sliderMax: data.get('sliderMax'),
    minInputSampleSize: data.get('minInputSampleSize')
  }
}

function dragArrow(payload = {}) {
  const { stepSize, positions, sliderMin, sliderMax, setSampleSize, dragHandleRef, sampleSizeInputRef, onUpdateRef } =
    payload
  const firstPosition = positions[0]
  const lastPosition = positions[positions.length - 1]
  const dragElement = dragHandleRef.current
  const mousedown = ObservableFromEvent(dragElement, 'mousedown')
  const mousemove = ObservableFromEvent(document, 'mousemove')
  const mouseup = ObservableFromEvent(document, 'mouseup')
  const clicks = rxMerge(mousedown.pipe(rxMapTo('startDrag')), mouseup.pipe(rxMapTo('stopDrag')))
  const mousedrag = mousedown.pipe(
    rxMergeMap(function (md) {
      const startX = md.clientX
      const startPositionID = positionIdBySampleSize(sampleSizeInputRef.current.value, positions)
      // calculate sampleSize
      return mousemove.pipe(
        rxMap(function (mm) {
          mm.preventDefault()
          const newPositionID = startPositionID + Math.round((mm.clientX - startX) / stepSize)
          return 0 > newPositionID
            ? firstPosition
            : positions.length - 1 < newPositionID
            ? lastPosition
            : positions[newPositionID]
        }),
        rxTakeUntil(mouseup)
      )
    })
  )
  // update sampleSize
  const dragSubscription = mousedrag.subscribe(
    sampleSize => void setSampleSize(validateSampleSize(sampleSize, sliderMin, sliderMax))
  )

  // update cursor style
  let lastClickValue = 'stopDrag'
  const clicksSubscription = clicks.subscribe(value => {
    if ('startDrag' === lastClickValue && 'stopDrag' == value) {
      dragElement.style.cursor = 'grab'
      dragElement.classList.toggle('active')
      if ('function' == typeof onUpdateRef.current) {
        onUpdateRef.current(sampleSizeInputRef.current.value)
      }
    } else if ('stopDrag' === lastClickValue && 'startDrag' == value) {
      dragElement.style.cursor = 'grabbing'
      dragElement.classList.toggle('active')
    }
    lastClickValue = value
  })

  return () => {
    clicksSubscription.unsubscribe()
    dragSubscription.unsubscribe()
  }
}

function getActiveRange(sampleSize, ranges) {
  return ranges.find(range => range.from <= sampleSize && sampleSize <= range.to)
}
function getSliderPosition(sampleSize, positions) {
  return (100 / positions.length) * positionIdBySampleSize(sampleSize, positions)
}
function validateSampleSize(sampleSize, minSampleSize, maxSampleSize) {
  const value = parseInt(sampleSize, 10) || 0
  return minSampleSize > value ? minSampleSize : maxSampleSize < value ? maxSampleSize : value
}
function positionIdBySampleSize(sampleSize, positions) {
  return positions.reduce(
    (prevID, _, currID) =>
      Math.abs(positions[currID] - sampleSize) < Math.abs(positions[prevID] - sampleSize) ? currID : prevID,
    0
  )
}
